import { NavLink } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Bell,
  Boxes,
  Briefcase,
  Building2,
  CalendarDays,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  FolderKanban,
  HardDrive,
  KeyRound,
  LayoutDashboard,
  Laptop,
  LifeBuoy,
  MessageSquare,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Target,
  User as UserIcon,
  UserCog,
  Video,
  Wallet,
  X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { GlobalSearch } from '@/components/layout/GlobalSearch';
import { ProfileMenu } from '@/components/layout/ProfileMenu';
import logo from '@/assets/logo.png';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  permission?: string;
  /** Platform Super Admin only (users.is_super_admin) — distinct from `permission`, which an Organization Admin can also satisfy. */
  superAdminOnly?: boolean;
}

const primaryNav: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/workspaces', label: 'Workspaces', icon: Boxes, permission: 'projects.view' },
  { to: '/projects', label: 'Projects', icon: FolderKanban, permission: 'projects.view' },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare, permission: 'tasks.view' },
  { to: '/time-tracking', label: 'Time Tracking', icon: Clock, permission: 'tasks.view' },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays, permission: 'calendar.view' },
  { to: '/meetings', label: 'Meetings', icon: Video, permission: 'meetings.view' },
  { to: '/chat', label: 'Chat', icon: MessageSquare, permission: 'chat.view' },
  { to: '/documents', label: 'Documents', icon: FileText, permission: 'documents.view' },
  { to: '/files', label: 'Files', icon: HardDrive, permission: 'files.view' },
  { to: '/reports', label: 'Reports', icon: BarChart3, permission: 'reports.view' },
  { to: '/crm', label: 'CRM', icon: Target, permission: 'crm.view' },
  { to: '/clients', label: 'Clients', icon: Briefcase, permission: 'clients.view' },
  { to: '/support-tickets', label: 'Support Tickets', icon: LifeBuoy, permission: 'support_tickets.view' },
  { to: '/organization', label: 'Organization', icon: Building2 },
];

const accountNav: NavItem[] = [
  { to: '/notifications', label: 'Notifications', icon: Bell },
  { to: '/profile', label: 'Profile', icon: UserIcon },
  { to: '/profile/password', label: 'Change Password', icon: KeyRound },
  { to: '/profile/two-factor', label: 'Two-Factor Auth', icon: ShieldCheck },
  { to: '/sessions', label: 'Session History', icon: Activity },
  { to: '/devices', label: 'Active Devices', icon: Laptop },
];

const adminNav: NavItem[] = [
  { to: '/roles', label: 'Role Management', icon: UserCog, permission: 'roles.view' },
  { to: '/permissions', label: 'Permission Management', icon: Shield, permission: 'roles.view' },
  { to: '/logs/activity', label: 'Activity Logs', icon: Activity },
  { to: '/logs/security', label: 'Security Logs', icon: ShieldAlert, permission: 'audit_logs.view' },
];

const platformAdminNav: NavItem[] = [
  { to: '/platform-admin/billing', label: 'Billing (Platform)', icon: Wallet, superAdminOnly: true },
];

function NavSection({ title, items, collapsed }: { title: string; items: NavItem[]; collapsed: boolean }) {
  return (
    <div>
      {!collapsed && (
        <p className="px-3 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-600">
          {title}
        </p>
      )}
      <nav className="flex flex-col gap-0.5">
        {items.map((item) => (
          <SidebarLink key={item.to} item={item} collapsed={collapsed} />
        ))}
      </nav>
    </div>
  );
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { user } = useAuth();
  const allowed = usePermission(item.permission ?? '__always__') || !item.permission;
  if (item.superAdminOnly && !user?.is_super_admin) {
    return null;
  }
  if (!allowed) {
    return null;
  }

  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-medium transition-colors ${
          collapsed ? 'justify-center' : ''
        } ${
          isActive
            ? 'bg-brand-600 text-white shadow-sm'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && item.label}
    </NavLink>
  );
}

function SidebarContent({
  collapsed,
  onToggleCollapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  /** Mobile drawer only — closes the drawer when a nav link is tapped, without closing it when the search/workspace/profile dropdowns are opened. */
  onNavigate?: () => void;
}) {
  const { user } = useAuth();
  return (
    <div className="flex h-full w-full flex-col gap-5 pt-4">
      <div className={`flex items-center gap-2 px-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5">
            <img src={logo} alt="ProjectPilot" className="h-full w-full object-cover object-left" />
          </div>
          {!collapsed && <span className="truncate text-base font-extrabold tracking-tight text-gray-900 dark:text-gray-100">ProjectPilot AI</span>}
        </div>
        {onToggleCollapsed && !collapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label="Collapse sidebar"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {onToggleCollapsed && collapsed && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label="Expand sidebar"
          className="mx-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}

      {!collapsed && (
        <div className="px-4">
          <GlobalSearch />
        </div>
      )}

      {/* No horizontal padding here — this is the scroll container, so the
          scrollbar renders flush against the sidebar's true right edge.
          Inset from the edges comes from pl-4/pr-2 instead, leaving a small
          gutter before the scrollbar rather than pushing it inward. */}
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-2 pl-4 pr-2" onClick={onNavigate}>
        <NavSection title="Workspace" items={primaryNav} collapsed={collapsed} />
        <NavSection title="Account" items={accountNav} collapsed={collapsed} />
        <NavSection title="Administration" items={adminNav} collapsed={collapsed} />
        {user?.is_super_admin && <NavSection title="Platform" items={platformAdminNav} collapsed={collapsed} />}
      </div>

      {!collapsed && (
        <div className="px-4 pb-4">
          <ProfileMenu variant="card" />
        </div>
      )}
    </div>
  );
}

/** Desktop sidebar — collapsible icon rail, per documentation/architecture/MASTER_ARCHITECTURE.md §5. */
export function Sidebar({ collapsed, onToggleCollapsed }: { collapsed: boolean; onToggleCollapsed?: () => void }) {
  return (
    <aside
      className={`sticky top-0 hidden h-screen shrink-0 overflow-hidden border-r border-gray-100 bg-white transition-[width] duration-200 dark:border-gray-800 dark:bg-gray-900 md:flex ${
        collapsed ? 'w-[76px]' : 'w-64'
      }`}
    >
      <SidebarContent collapsed={collapsed} onToggleCollapsed={onToggleCollapsed} />
    </aside>
  );
}

/** Mobile off-canvas drawer — same nav content, full width, dismissible. */
export function MobileSidebarDrawer({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
          />
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-2xl dark:bg-gray-900 md:hidden"
          >
            <div className="flex items-center justify-between border-b border-gray-100 p-3 dark:border-gray-800">
              <span className="px-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Menu</span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="h-[calc(100%-53px)]">
              <SidebarContent collapsed={false} onNavigate={onClose} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
